import React from 'react';
import pluralize from 'pluralize';
import { makeStyles } from '@material-ui/core';
import { transparentize } from 'polished';
import { Theme } from 'styles/ThemeTyping';

import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import InfoIcon from '@material-ui/icons/Info';
import VisibilityIcon from '@material-ui/icons/Visibility';
import Tooltip from '@material-ui/core/Tooltip';
import Chip from './Chip';

interface Props {
  label: string;
  memberCount?: number;
  disabledMembersEdit?: boolean;
  disabledPermissionsEdit?: boolean;
  truncationDisabled?: boolean;
  className?: string;
  onDelete?: () => void;
  onEdit?: () => void;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

function ChipRow({
  label,
  memberCount,
  disabledMembersEdit,
  disabledPermissionsEdit,
  truncationDisabled,
  className,
  onDelete,
  onEdit,
  onClick,
}: Props) {
  const classes = useStyles();
  const membersLabel = pluralize(label, memberCount);
  const chipLabel = memberCount ? membersLabel : label;

  return (
    <div className={classes.root}>
      <Chip
        label={chipLabel}
        onClick={onClick}
        className={className}
        truncationDisabled={truncationDisabled}
      />
      <div>
        {disabledMembersEdit ? (
          <div className={classes.prebuiltTag}>
            <Typography variant="caption">Cannot be edited</Typography>

            <InfoIcon className={classes.icon} />
          </div>
        ) : (
          <>
            {onEdit && disabledPermissionsEdit ? (
              <Tooltip title="View" placement="top" arrow>
                <IconButton className={classes.icon} onClick={onEdit}>
                  <VisibilityIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Edit" placement="top" arrow>
                <IconButton
                  className={classes.icon}
                  onClick={onEdit}
                  data-cy={`edit-${chipLabel}-chip-button`}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}

            {onDelete && !memberCount && (
              <Tooltip title="Remove" placement="top" arrow>
                <IconButton
                  className={classes.icon}
                  onClick={onDelete}
                  data-cy={`delete-${chipLabel}-chip-button`}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: 1022,
    width: '100%',
    height: 52,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.75),
    borderColor: transparentize(0.9, theme.palette.common.black),
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: 4,
    marginBottom: theme.spacing(),
  },
  icon: {
    maxHeight: 20,
    maxWidth: 20,
    '&:last-child': {
      marginLeft: theme.spacing(1.5),
    },
  },
  prebuiltTag: {
    display: 'flex',
    alignItems: 'center',
    color: theme.colors.gray2,
  },
}));

export default ChipRow;
